import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { examDefault } from 'src/app/interface/dtoDefaults';
import { ActivatedRoute, Router } from '@angular/router';
import { IExam, IExamCategory, IExamQuestionOptions, IExamQuestions, IExamQuestionEssaySubQuestion, IQuestionGroup } from 'src/app/interface/dto';
import { ExamService } from 'src/app/services/exam-service.service';
import { Location } from '@angular/common';
import { SnackbarService } from 'src/app/services/snackbar.service';
import { ConfirmDialogService } from 'src/app/services/confirm-dialog.service';
import { ServiceResponse } from 'src/app/models/service-response';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-create-questions',
  templateUrl: './create-questions.component.html',
  styleUrls: ['./create-questions.component.scss']
})
export class CreateQuestionsComponent implements OnInit, OnChanges {
  questionGroups: IQuestionGroup[] = [];
  textDisplay = 'Add New Question';

  exam: IExam = examDefault;

  modelError = '';
  essayexam = false;
  issaq = false;
  multiple = false;
  question: IExamQuestions = {
    examinationQuestionID: 0,
    examinationID: 0,
    questionNumber: 0,
    questionType: '',
    questionHtml: '',
    marks: 1,
    penaltyMarks: 0,
    disabled: false,
    options: [],
    category: '',
    saqPossibleAnsers: '',
    language: 'en',
    maxWordCount: 0,
    languageEnum: '',
    compulsory: false,
    essaySubQuestions: []
  };
  @Input() selectedQuestion = {} as IExamQuestions;
  questionAdvanceMode: boolean = false;
  optionAdvanceMode: boolean = false;
  languages: any = [{ lang: 'English', code: 'en' }, { lang: 'Français', code: 'fr' }, { lang: 'Not Set', code: 'NotSet' }];
  lastCategory: string = '';
  isSave = true;
  leftWidth = 650; // default width in px
  isDragging = false;
  startX = 0;
  startWidth = 0;
  get useQuestionBank(): boolean {
    return this.exam?.useQuestionBank || false;
  }

  get isPsychometricTestBank(): boolean {
    return this.exam?.psychometricTest || false;
  }

  constructor(private examService: ExamService, private snackBar: SnackbarService,
    private actRoute: ActivatedRoute, private _location: Location, private cdr: ChangeDetectorRef,
    private confirmDialog: ConfirmDialogService) { }

  async ngOnInit() {

    const routeParams = await firstValueFrom(this.actRoute.params);    
    const examID = routeParams['examid'];
    
    if (this.examService.selectedExams?.length === 0) {
      let selectedExam = await this.examService.getExamById(examID) as IExam;
      this.examService.setSelectedExam([selectedExam]);
    }
    
    if (this.examService.selectedExams?.length === 1) {
      this.exam = this.examService.selectedExams[0];

      await this.examService.getExamCategories(this.exam.id).then((categories: any) => {
        this.lastCategory = categories[categories.length - 1]?.category;

      });
      const queryParams = await firstValueFrom(this.actRoute.queryParams);
      if (queryParams['id']) {
        this.question.examinationQuestionID = JSON.parse(queryParams['id']) as number ?? 0;
        this.issaq = this.exam.examinationTypeenum == "SAQ";
        this.essayexam = this.exam.examinationTypeenum == "Essay";
        if (this.question.examinationQuestionID !== 0 && this.question.examinationQuestionID !== null) {
          this.textDisplay = 'Update Question';
          this.isSave = false;
        }
      } else {
        this.pushOption();
      }
      this.leftWidth = parseInt(localStorage.getItem(`createQuestionColumnWidth`)) || this.leftWidth;
      await this.getQuestionDetails();
    }

    if(!this.exam.psychometricTest) {
    this.questionGroups = await this.examService.getQuestionGroupsLookup(examID) as IQuestionGroup[];
    }
  }

  pushOption() {
    if (!this.question.options) {
      this.question.options = [];
    }
    this.question.options.push(
      {
        examinationQuestionID: 0,
        optionID: 0,
        optionHtml: '',
        answer: false
      },
      {
        examinationQuestionID: 0,
        optionID: 0,
        optionHtml: '',
        answer: false
      },
      {
        examinationQuestionID: 0,
        optionID: 0,
        optionHtml: '',
        answer: false
      }
    );
  }

  removeOption(index) {
    const halfBeforeTheUnwantedElement = this.question.options.slice(0, index)

    index += 1;
    const halfAfterTheUnwantedElement = this.question.options.slice(index);

    this.question.options = halfBeforeTheUnwantedElement.concat(halfAfterTheUnwantedElement);
  }

  pushEssaySubQuestion() {
    if (!this.question.essaySubQuestions) {
      this.question.essaySubQuestions = [];
    }

    const index = this.question.essaySubQuestions.length;

    const newEssaySubQuestion: IExamQuestionEssaySubQuestion = {
      examinationQuestionID: this.question.examinationQuestionID,
      essaySubQuestionID: 0, // new, unsaved
      questionHtml: '',
      questionNoLabel: this.getLetter(index),
      marks: 0
    };

    this.question.essaySubQuestions.push(newEssaySubQuestion);
  }

  getLetter(index: number): string {
    return String.fromCharCode(97 + index); // 97 = 'a'
  }

  removeEssaySubQuestion() {
    if (!this.question.essaySubQuestions || this.question.essaySubQuestions.length === 0) {
      return;
    }

    // Only remove the last Sub-Question
    const essaySubQuestion = this.question.essaySubQuestions;
    this.question.essaySubQuestions = essaySubQuestion.slice(0, essaySubQuestion.length - 1);
  }

  async saveQuestion(update: boolean = false) {
    this.question.questionType = GetQuestionType(this.exam.examinationTypeenum);
    this.validateModel();
    if (this.modelError !== '') {
      window.document.getElementById("modelError").focus();
      window.document.getElementById("modelError").blur();
      return
    }
    this.question.examinationID = this.exam.id;

    const questionRequest = {
      QuestionsModel: this.question
    };

    this.question.options = this.question.options?.filter(q => this.htmlStringIsNotBlank(q.optionHtml)); // Remove empty options

    await this.processQuestionRequest(questionRequest, update);
  }

  private async processQuestionRequest(questionRequest: any, update: boolean) {
    try {
      const serviceCall = update
        ? this.examService.updateQuestion(questionRequest)
        : this.examService.saveQuestion(questionRequest);

      const response: ServiceResponse = await serviceCall;

      if (response.success) {
        if (response.data === null) {
          // Show confirmation modal when data is null but success is true
          const confirmed = await this.confirmDialog.open({
            message: response.message || 'There may be validation warnings. Do you want to proceed anyway?',
            confirmText: 'Yes, Proceed',
            cancelText: 'Cancel',
            size: 'lg',
            centered: true,
            title: 'Confirm Action',
          });

          if (confirmed) {
            // Retry with IgnoreErrors = true
            const retryRequest = {
              ...questionRequest,
              IgnoreErrors: true
            };
            await this.processQuestionRequest(retryRequest, update);
          }
        } else {
          // Normal success case
          const successMessage = update
            ? 'Question Updated Successfully!'
            : 'Question Saved Successfully!';
          this.snackBar.success(response.message || successMessage);
          this.examService.reloadGrid.next(true);
        }
      } else {
        this.snackBar.error(response.message || 'Operation failed');
      }
    } catch (error) {
      console.error('Question operation error:', error);
      this.snackBar.error(error);
    }
  }

  validateModel() {
    this.question.options = this.question.options?.filter(q => q.optionHtml !== '');

    this.modelError = ''
    if (this.question.questionHtml == '') {
      this.modelError += "\n*Question required \n"
    }
    if (this.question.marks == 0) {
      this.modelError += "\n*Marks required \n"
    }
    if (this.question.questionType == '') {
      this.modelError += "\n*Question Type required \n"
    }
    if (!this.question.options?.find(q => q.answer === true) && this.question.questionType !== 'SAQ' && this.question.questionType !== 'Essay') {
      this.modelError += "\n*No answer Selected \n"
    }
    if (this.question.options?.find(q => q.answer == true && q.optionHtml == '') && this.question.questionType !== 'SAQ' && this.question.questionType !== 'Essay') {
      this.modelError += "\n*answer has no value \n"
    }
    if (this.question.options?.length < 2 && this.question.questionType !== 'SAQ' && this.question.questionType !== 'Essay') {
      this.modelError += "\n*No option inputed. Must be more than 2 \n"
    }

    let totalEssaySubQuestionMarks = 0;

    this.question.essaySubQuestions?.forEach((subQuestion, index) => {
      const label = subQuestion.questionNoLabel || this.getLetter(index);

      if (this.htmlStringIsNotBlank(subQuestion.questionHtml) == '') {
        this.modelError += `\n* Essay Sub-Question ${label}. content is required\n`;
      }

      if (subQuestion.marks == null || subQuestion.marks === 0) {
        this.modelError += `\n* Essay Sub-Question ${label}. marks is required\n`;
      } else {
        totalEssaySubQuestionMarks += subQuestion.marks;
      }
    });

    if (totalEssaySubQuestionMarks > this.question.marks) {
      this.modelError += `\n* Total marks of Essay Sub-Questions (${totalEssaySubQuestionMarks}) exceed main question marks (${this.question.marks})\n`;
    }
  }

  async ngOnChanges(changes: SimpleChanges) {
    if (changes['selectedQuestion'] && changes['selectedQuestion'].currentValue) {

      this.question = { ...changes['selectedQuestion'].currentValue };

      if (this.question.examinationQuestionID) {
        this.textDisplay = 'Update Question';
        this.isSave = false;
        await this.getQuestionDetails();
      } else {
        this.textDisplay = 'Add New Question';
        this.optionAdvanceMode = false;
        this.questionAdvanceMode = false;
        this.isSave = true;
        this.question.category = this.lastCategory;
        this.question.options = Array.from({ length: 4 }, () => ({
          optionHtml: '',
          answer: false,
          examinationQuestionID: 0,
          optionID: 0
        }));
      }

    }
    this.cdr.detectChanges();
    // console.log(this.question,"Q");

  }
  async getQuestionDetails() {
    this.optionAdvanceMode = false;
    this.questionAdvanceMode = false;
    if (!this.isSave) {
      await this.examService.getQuestion(this.question.examinationQuestionID).then((q: any) => {
        this.question = q;
        // console.log("Question", q);
        if (this.question.questionType === "Short Answer Question") {
          this.question.questionType = "SAQ"
        }
        if (this.isHtml(this.question.questionHtml)) {
          this.questionAdvanceMode = true;
        }
        let optionWithHtml = this.question.options?.find(o => this.isHtml(o.optionHtml));
        if (optionWithHtml) {
          this.optionAdvanceMode = true;
        }
        this.question.language = this.question.languageEnum
        this.answerMarked();
      })
    }
  }
  cancel() {
    this._location.back();
  }

  htmlStringIsNotBlank(htmlString: string) {
    if (!htmlString || !htmlString.trim()) {
      return ''; // Return an empty string if the input is empty or whitespace
    }

    // Create a temporary DOM element
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlString;

    // Remove script and style elements to exclude their content
    tempDiv.querySelectorAll('script, style').forEach((element) => element.remove());

    // Get the text content from the remaining HTML
    return !!tempDiv.textContent.trim();
  }
  async replaceQuestion() {
    if (!this.question.examinationQuestionID) {
      this.snackBar.error("No question selected to replace.");
      return;
    }

    try {
      await this.examService.replaceQuestionFromQuestionBank(this.question.examinationQuestionID);
      this.getQuestionDetails();
      this.snackBar.success("Question replaced successfully!");
      this.cdr.detectChanges();
    } catch (error) {
      this.snackBar.error(`Error replacing question: ${error.message}`);
      console.error('Error:' + error);

    }
  }
  answerMarked() {
    setTimeout(() => {
      let answers = this.question.options?.filter(o => o.answer);
      this.multiple = answers.length > 1;
    });
  }
  easyModeClicked(type: number = 0) {

    switch (type) {
      case 1:
        this.questionAdvanceMode = !this.questionAdvanceMode;
        break;
      case 2:
        this.optionAdvanceMode = !this.optionAdvanceMode;
        break;
      default:
        break;
    }
  }

  startDragging(event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = true;
    this.startX = event.clientX;
    this.startWidth = this.leftWidth;

    const mouseMove = (e: MouseEvent) => {
      if (!this.isDragging) return;

      const delta = e.clientX - this.startX;
      const newWidth = this.startWidth + delta;

      if (newWidth >= 350) {
        this.leftWidth = newWidth;
        localStorage.setItem(`createQuestionColumnWidth`, this.leftWidth.toString());
      }
    };

    const stop = () => {
      this.isDragging = false;
      document.removeEventListener("mousemove", mouseMove);
      document.removeEventListener("mouseup", stop);
    };

    document.addEventListener("mousemove", mouseMove);
    document.addEventListener("mouseup", stop);
  }

  isHtml(content: string): boolean {
    return /<\/?[a-z][\s\S]*>/i.test(content);
  }
  async createNewQuestion() {

    try {
      await this.saveQuestion(!this.isSave);
    } catch (error) {
      this.snackBar.error(`Error saving question: ${error.message}`);
      console.error('Error:' + error);

      return;
    }

    this.textDisplay = 'Add New Question';
    this.isSave = true;
    this.question = {
      examinationQuestionID: 0,
      examinationID: 0,
      questionNumber: 0,
      questionType: '',
      questionHtml: '',
      marks: 1,
      penaltyMarks: 0,
      disabled: false,
      options: [],
      category: '',
      saqPossibleAnsers: '',
      language: 'en',
      maxWordCount: 0,
      languageEnum: '',
      compulsory: false,
      essaySubQuestions: []
    }
    this.pushOption();
    this.optionAdvanceMode = false;
    this.questionAdvanceMode = false;
  }
}


function GetQuestionType(examinationTypeenum: string): string {
  switch (examinationTypeenum) {
    case 'MultipleChoice':
      return 'OptionsOnly';
    case 'Essay':
      return 'Essay';
    case 'SAQ':
      return 'SAQ';
    default: 'NotSet'
  }
}

